import React, { useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  TextInput,
} from '@patternfly/react-core';
import { PaperPlaneIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useUserContext } from '../../../contexts/user/UserContext';
import type { NewsletterSummary } from '../../../types/models/Newsletter';
import type { ApiResponseNewsletterTest } from '../../../types/api/ApiResponseNewsletter';

interface NewsletterPreviewPopoverBodyProps {
  newsletter: NewsletterSummary;
  /** Close the popover (called after a proof is sent). */
  onDone: () => void;
}

// Lightweight client-side check; the backend is authoritative (filter_var FILTER_VALIDATE_EMAIL).
const looksLikeEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/**
 * Body of the "Preview" popover: sends ONE proof copy of the newsletter to an address via
 * `POST …/test` (staff proofing — renders the real newsletter, does not change its status). Defaults
 * to the logged-in staffer's email but stays editable.
 */
function NewsletterPreviewPopoverBody({ newsletter, onDone }: NewsletterPreviewPopoverBodyProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const user = useUserContext();

  const [email, setEmail] = useState(user?.email ?? '');
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const canSend = !isSending && looksLikeEmail(email);

  const handleSend = async () => {
    setIsSending(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/newsletters/${newsletter.id}/test`, {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      });
      // Branch on the JSend `body.status`, NEVER `response.ok`.
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterTest | null;

      if (body?.status === 'success' && body.data?.sent) {
        addNotification({
          title: 'Preview sent',
          srTitle: 'Success alert',
          description: `A preview of ${newsletter.subject} was sent to ${body.data.to ?? email.trim()}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onDone();
        return;
      }

      // Field errors: `email` (invalid), `template` (render failure).
      const fieldMessage =
        typeof body?.data?.email === 'string'
          ? (body.data.email as string)
          : typeof body?.data?.template === 'string'
            ? (body.data.template as string)
            : null;
      const message = fieldMessage ?? body?.message ?? 'Could not send the preview. Please try again.';
      setError(message);
      addNotification({
        title: 'Could not send preview',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(message);
      addNotification({
        title: 'Could not send preview',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        if (canSend) {
          void handleSend();
        }
      }}
    >
      <Content component="p">Send a one-off proof of this newsletter to check how it looks.</Content>
      {error !== null && (
        <Alert variant="danger" isInline title="Preview failed" className="pf-v6-u-mb-sm">
          {error}
        </Alert>
      )}
      <FormGroup label="Send preview to" isRequired fieldId="newsletter-preview-email">
        <TextInput
          id="newsletter-preview-email"
          type="email"
          value={email}
          onChange={(_event, value) => {
            setEmail(value);
            setError(null);
          }}
          isDisabled={isSending}
          placeholder="you@example.com"
        />
        <FormHelperText>
          <HelperText>
            <HelperTextItem>Defaults to your address — edit it to send the proof elsewhere.</HelperTextItem>
          </HelperText>
        </FormHelperText>
      </FormGroup>
      <Button
        type="submit"
        variant="primary"
        icon={<PaperPlaneIcon />}
        isDisabled={!canSend}
        isLoading={isSending}
        className="pf-v6-u-mt-sm"
      >
        Send Preview
      </Button>
    </Form>
  );
}

export default NewsletterPreviewPopoverBody;
